import type { FiscalQuota } from "@/domain/beneficiary";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { SelectOption } from "@/presentation/pages/investments/config/investmentFormOptions";
import { quotaName } from "@/utils/quotaName";

interface BeneficiaryOption {
  value: string;
  label: string;
  disabled?: boolean;
}

interface UseInvestmentDropdownOptionsProps {
  beneficiaryOptions: BeneficiaryOption[];
  availableFiscalQuotas: FiscalQuota[];
  incentiveType: "film_audiovisual" | "performing_arts_music" | null;
}

interface UseInvestmentDropdownOptionsReturn {
  projectOptions: SelectOption[];
  quotaOptions: SelectOption[];
}

/**
 * Hook that computes memoized dropdown options for the investment form.
 * Transforms raw data (beneficiaries, fiscal quotas) into SelectOption arrays.
 * Investor options come from useInvestorDropdown (async server-side search).
 */
export function useInvestmentDropdownOptions({
  beneficiaryOptions,
  availableFiscalQuotas,
  incentiveType,
}: UseInvestmentDropdownOptionsProps): UseInvestmentDropdownOptionsReturn {
  const { t } = useTranslation();

  const projectOptions = useMemo((): SelectOption[] => {
    return beneficiaryOptions.map((opt) => ({
      value: opt.value,
      label: opt.label,
      disabled: opt.disabled,
    }));
  }, [beneficiaryOptions]);

  const quotaOptions = useMemo((): SelectOption[] => {
    return availableFiscalQuotas.map((quota) => ({
      value: quota.uuid,
      label: quotaName(quota, incentiveType, t),
    }));
  }, [availableFiscalQuotas, incentiveType, t]);

  return { projectOptions, quotaOptions };
}
